import { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { Checkbox, StatusBar, PrimaryButton, SnackbarStatus, Spinner, Typography } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import ModalWrapper from '../ModalWrapper';
import { ILinkVenuesModalProps, ILoadedVenuesProps } from './';
import { updateMenuVenues } from 'src/api/updateMenuVenues/updateMenuVenues';
import { IUpdateMenuVenuesRequest } from 'src/api/updateMenuVenues/types';
import { IMenu } from 'src/types/menu.types';
import ModalImg from '../../assets/images/chef-plating.png';
import styles from './LinkVenuesModal.module.scss';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { useVenues } from 'src/hooks/useVenues/useVenues';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';

const LoadedVenues = ({ venues, register }: ILoadedVenuesProps): ReactElement => (
  <>
    {venues?.length > 0 ? (
      venues.map((linkedVenue) => (
        <div key={`${linkedVenue.name}-${linkedVenue.venueId}`} className={styles['venue-wrapper']}>
          <Checkbox key={linkedVenue.venueId} label={linkedVenue.name} name={linkedVenue.venueId} register={register} />
        </div>
      ))
    ) : (
      <Typography size="small">Add your first venue</Typography>
    )}
  </>
);

const LinkVenuesFlow = ({ closeModal, menu, status, menuId }: Omit<ILinkVenuesModalProps, 'open'>): ReactElement => {
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { trackEvent } = useUserTracking();
  const { data: venues, isLoading } = useVenues();
  const { refetch } = useMenu(menuId);

  const formattedVenues = venues?.map((venue) => ({ name: venue.name, venueId: venue.venue_id }));

  const getInitialValues = (menu: IMenu): Record<string, boolean> =>
    // TODO - After upgrading to TS 5.x you will be able to call .reduce directly after .values() without spreading the
    //        iterator into an array
    [...menu.linkedVenueIds].reduce<Record<string, boolean>>(
      (acc, venueId) => ({
        ...acc,
        [venueId]: true,
      }),
      {},
    );

  const { handleSubmit, register } = useForm<Record<string, boolean>>({
    defaultValues: getInitialValues(menu),
    mode: 'onChange',
  });

  const onSave = async (data: Record<string, boolean>) => {
    clearError();
    const checkedVenues: string[] = Object.keys(data).filter((venueId) => data[venueId]);

    const requestBody: IUpdateMenuVenuesRequest = {
      venue_ids: checkedVenues,
    };

    await updateMenuVenues(menuId, requestBody)
      .then(() => {
        trackEvent('menu_venues_linked', { menuId, venueCount: checkedVenues.length });
        refetch();
        closeModal();
      })
      .catch(() => {
        showNotification({
          status: SnackbarStatus.ERROR,
          message: 'We have been unable to complete your request. Please try again',
        });
      });
  };

  return (
    <form name="linked-venues" onSubmit={handleSubmit(onSave)}>
      <main>
        <Typography weight="semi-bold" size="xxlarge" className={styles.heading}>
          Link venues
        </Typography>
        <Typography weight="semi-bold" className={styles['link-menu-text']}>
          Link menu to:
        </Typography>
        {isLoading || status === 'loading' ? (
          <div className={styles['loading-container']}>
            <Spinner size="xlarge" data-testid="link-venues-loading" />
          </div>
        ) : (
          <LoadedVenues venues={formattedVenues} register={register} />
        )}
        <div className={styles['button-container']}>
          <PrimaryButton type="submit" size="full-width">
            Save
          </PrimaryButton>
        </div>
        <div className={styles['error-banner']}>
          {errorMessage && <StatusBar message={errorMessage} variant="error" />}
        </div>
      </main>
    </form>
  );
};

const LinkVenuesModal = ({ closeModal, menu, open, status, menuId }: ILinkVenuesModalProps): ReactElement => {
  return (
    <ModalWrapper closeModal={closeModal} open={open} image={ModalImg} imageAlt="Chef preparing dishes">
      <LinkVenuesFlow closeModal={closeModal} menuId={menuId} status={status} menu={menu} />
    </ModalWrapper>
  );
};

export default LinkVenuesModal;
